import { Fragment, useState } from "react";
import {
  Scale,
  Search,
  Plus,
  Filter,
  ShieldCheck,
  AlertTriangle,
  ChevronDown,
  Info,
  ExternalLink,
  BookOpen
} from "lucide-react";
import { useCapabilities } from "../../lib/permissions/useCapabilities";

type Region = "all" | "us" | "eu";
type PolicyStatus = "active" | "warning" | "info";

export interface Policy {
  category: string;
  region: "US" | "EU" | "US & EU";
  deductibility: string;
  vatRule: string;
  requirements: string[];
  status: PolicyStatus;
  note?: string;
  reference: string;
  referenceUrl: string;
}

export const ALL_POLICIES: Policy[] = [
  // ── US IRS Policies ──────────────────────────────────────────────
  {
    category: "Business Meals",
    region: "US",
    deductibility: "50% (100% from restaurants 2025–2026)",
    vatRule: "N/A (no federal VAT)",
    requirements: ["Itemized Receipt", "Business Purpose", "Attendee List"],
    status: "warning",
    note: "The One Big Beautiful Bill Act temporarily restores the 100% restaurant-meal deduction for expenses incurred Jan 1, 2025 – Dec 31, 2026. Otherwise 50% applies.",
    reference: "IRS Pub. 463 §§ 2, 50%; One Big Beautiful Bill Act (2025)",
    referenceUrl: "https://www.irs.gov/publications/p463",
  },
  {
    category: "Entertainment",
    region: "US",
    deductibility: "0% (non-deductible)",
    vatRule: "N/A",
    requirements: ["N/A — not deductible"],
    status: "warning",
    note: "Client entertainment (tickets, golf, clubs) is fully non-deductible under TCJA 2017. Meals at entertainment venues are still 50% if separately itemized.",
    reference: "IRC § 274(a); TCJA 2017",
    referenceUrl: "https://www.irs.gov/taxtopics/tc512",
  },
  {
    category: "Business Travel (Airfare & Lodging)",
    region: "US",
    deductibility: "100% (business portion)",
    vatRule: "N/A",
    requirements: ["Flight/Train Itinerary", "Hotel Folio", "Business Purpose"],
    status: "active",
    note: "Travel must be away from tax home overnight for business. Personal portions are not deductible. Spouse/family travel deductible only if bona fide employee on a business trip.",
    reference: "IRS Pub. 463; IRC § 162; Topic No. 511",
    referenceUrl: "https://www.irs.gov/taxtopics/tc511",
  },
  {
    category: "Vehicle / Mileage",
    region: "US",
    deductibility: "67¢/mile (2024) or Actual Expenses",
    vatRule: "N/A",
    requirements: ["Mileage Log", "Business Purpose per Trip"],
    status: "active",
    note: "Standard mileage rate is 67¢/mile for 2024 (IRS Rev. Proc. 2023-34). Alternatively, deduct actual gas, insurance, depreciation etc. proportional to business use. Section 179 / bonus depreciation available for vehicles placed in service.",
    reference: "IRS Rev. Proc. 2023-34; IRS Pub. 463; IRC § 179",
    referenceUrl: "https://www.irs.gov/publications/p463",
  },
  {
    category: "Client Gifts",
    region: "US",
    deductibility: "Limited — $25/recipient/year",
    vatRule: "N/A",
    requirements: ["Receipt", "Recipient Name", "Business Relationship"],
    status: "warning",
    note: "Annual cap is $25 per person regardless of actual cost (in place since 1962). Incidental costs such as engraving and gift wrapping may be deducted beyond the $25 cap. Widely distributed promotional items (e.g., branded pens) are generally fully deductible.",
    reference: "IRC § 274(b); IRS Pub. 463 § Gifts",
    referenceUrl: "https://www.irs.gov/publications/p463",
  },
  {
    category: "Software & SaaS Subscriptions",
    region: "US",
    deductibility: "100% (Section 179 or expensed)",
    vatRule: "N/A",
    requirements: ["Invoice or Subscription Agreement", "Business Use Confirmation"],
    status: "active",
    note: "Off-the-shelf software qualifies for immediate expensing under Section 179 (up to $1,220,000 for 2024). Custom software may need to be amortized over 3 years (IRC § 197). Recurring SaaS fees are ordinary business expenses fully deductible in the year paid.",
    reference: "IRC § 179; IRC § 197; IRS Pub. 946",
    referenceUrl: "https://www.irs.gov/publications/p946",
  },
  {
    category: "Office Supplies & Equipment",
    region: "US",
    deductibility: "100%",
    vatRule: "N/A",
    requirements: ["Receipt"],
    status: "active",
    note: "Ordinary and necessary supplies are fully deductible in the year purchased. Equipment may be expensed immediately via Section 179 (up to $1,220,000 in 2024) or depreciated under MACRS.",
    reference: "IRC § 162; IRC § 179; IRS Pub. 946",
    referenceUrl: "https://www.irs.gov/publications/p946",
  },
  {
    category: "Home Office",
    region: "US",
    deductibility: "100% direct / Proportional indirect",
    vatRule: "N/A",
    requirements: ["Floor Plan / Square Footage", "Exclusive Business Use Proof"],
    status: "info",
    note: "Simplified method: $5/sq ft, up to 300 sq ft ($1,500 max). Regular method: actual expenses × (office sq ft ÷ home sq ft). Space must be used exclusively and regularly for business. W-2 employees are not eligible after TCJA 2017.",
    reference: "IRC § 280A; IRS Pub. 587",
    referenceUrl: "https://www.irs.gov/publications/p587",
  },
  {
    category: "Professional Development & Education",
    region: "US",
    deductibility: "100%",
    vatRule: "N/A",
    requirements: ["Receipt / Enrollment Confirmation", "Business Relevance Statement"],
    status: "active",
    note: "Courses, conferences, and certifications that maintain or improve skills required in your current business are fully deductible. Education that qualifies for a new career is not deductible.",
    reference: "IRC § 162; IRS Pub. 970",
    referenceUrl: "https://www.irs.gov/publications/p970",
  },
  {
    category: "Advertising & Marketing",
    region: "US",
    deductibility: "100%",
    vatRule: "N/A",
    requirements: ["Invoice", "Campaign Summary or Description"],
    status: "active",
    note: "All ordinary and necessary advertising costs — digital ads, print, agency fees, website hosting — are fully deductible as business expenses.",
    reference: "IRC § 162; IRS Pub. 535",
    referenceUrl: "https://www.irs.gov/publications/p535",
  },
  // ── EU VAT Policies ──────────────────────────────────────────────
  {
    category: "Business Meals",
    region: "EU",
    deductibility: "50–80% (varies by member state)",
    vatRule: "Standard Rate — VAT often non-deductible",
    requirements: ["Original VAT Invoice", "Business Purpose", "Attendee List"],
    status: "warning",
    note: "Most EU member states restrict meal deductibility. Germany: 70% deductible. Netherlands / Belgium: 80%. France: 80% for meals, 50% for representation events. Local VAT on restaurant bills is typically non-deductible or only partially recoverable.",
    reference: "EU VAT Directive 2006/112/EC Art. 176; national rules",
    referenceUrl: "https://taxation-customs.ec.europa.eu/taxation/vat/eu-vat-rule-topic_en",
  },
  {
    category: "Entertainment & Client Events",
    region: "EU",
    deductibility: "0–50% (highly restricted)",
    vatRule: "Non-deductible VAT",
    requirements: ["Invoice", "Guest List", "Business Purpose"],
    status: "warning",
    note: "Entertainment VAT is broadly excluded from recovery across EU member states under Art. 176 of the VAT Directive. Corporate tax deductibility also varies significantly; many countries cap or fully disallow representation costs.",
    reference: "EU VAT Directive 2006/112/EC Art. 176",
    referenceUrl: "https://taxation-customs.ec.europa.eu/taxation/vat/eu-vat-rule-topic_en",
  },
  {
    category: "Business Travel (Flights & Hotels)",
    region: "EU",
    deductibility: "100% (business portion)",
    vatRule: "Flights: Zero-rated; Hotels/Car Hire: Standard Rate (reclaimable)",
    requirements: ["Valid VAT Invoice", "Flight Itinerary", "Hotel Folio", "Business Purpose"],
    status: "active",
    note: "Air travel within and departing the EU is zero-rated (no VAT to reclaim). Hotel accommodation, car hire, and inter-city rail carry local VAT which may be reclaimed via direct registration or the EU cross-border VAT refund procedure (Directive 2008/9/EC).",
    reference: "EU VAT Directive 2006/112/EC; Directive 2008/9/EC",
    referenceUrl: "https://europa.eu/youreurope/business/taxation/vat/cross-border-vat/index_en.htm",
  },
  {
    category: "Software & Digital Services (B2B Cross-border)",
    region: "EU",
    deductibility: "100%",
    vatRule: "Reverse Charge (EU VAT MOSS / OSS)",
    requirements: ["Valid Invoice with Supplier VAT No.", "Customer VAT No."],
    status: "active",
    note: "When a VAT-registered EU business purchases software or digital services from a supplier in another EU country, the reverse charge applies: the buyer self-accounts for VAT at their local rate. If the expense is fully business-related, the buyer also claims full input VAT, resulting in zero net VAT. Non-EU suppliers (e.g., US SaaS vendors) are subject to the same mechanism.",
    reference: "EU VAT Directive 2006/112/EC Art. 44, 196; EU OSS rules",
    referenceUrl: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=celex%3A32006L0112",
  },
  {
    category: "Office Supplies & Equipment",
    region: "EU",
    deductibility: "100%",
    vatRule: "Standard Rate — input VAT fully reclaimable",
    requirements: ["Valid VAT Invoice"],
    status: "active",
    note: "Input VAT on office supplies and equipment used exclusively for taxable business activities is fully deductible on the periodic VAT return. Mixed-use items require apportionment.",
    reference: "EU VAT Directive 2006/112/EC Art. 168",
    referenceUrl: "https://taxation-customs.ec.europa.eu/archives/taxable-persons/deductions_en",
  },
  {
    category: "Company Vehicles",
    region: "EU",
    deductibility: "50% (typical; 100% if exclusively business)",
    vatRule: "Standard Rate — 50% VAT typically reclaimable",
    requirements: ["Lease / Purchase Invoice", "Mileage / Usage Log", "Business Purpose"],
    status: "warning",
    note: "Most EU member states restrict vehicle VAT recovery to 50% for cars available for private use. A vehicle log showing 100% business use may allow full recovery. Germany limits car VAT recovery to 50% by default; France limits to 0% for passenger cars unless exclusively for commercial use.",
    reference: "EU VAT Directive 2006/112/EC Art. 176; national implementing rules",
    referenceUrl: "https://taxation-customs.ec.europa.eu/taxation/vat/eu-vat-rule-topic_en",
  },
  {
    category: "Client Gifts",
    region: "EU",
    deductibility: "Limited — €35–€227/recipient/year (varies by country)",
    vatRule: "Standard Rate — non-deductible above threshold",
    requirements: ["Receipt", "Recipient Name", "Business Relationship"],
    status: "warning",
    note: "Many EU member states allow a small deduction threshold for business gifts: Germany €35/recipient/year, France €73/recipient/year, Netherlands €227/year for promotional gifts. VAT is not reclaimable on gifts above the threshold as they are treated as taxable supplies.",
    reference: "National corporate income tax regulations; EU VAT Directive Art. 16",
    referenceUrl: "https://taxation-customs.ec.europa.eu/taxation/vat/eu-vat-rule-topic_en",
  },
];

const REFERENCES = [
  { label: "IRS Publication 463 — Travel, Gift & Car Expenses", url: "https://www.irs.gov/publications/p463" },
  { label: "IRS Publication 535 — Business Expenses", url: "https://www.irs.gov/publications/p535" },
  { label: "IRS Publication 946 — How to Depreciate Property", url: "https://www.irs.gov/publications/p946" },
  { label: "IRS Publication 587 — Business Use of Your Home", url: "https://www.irs.gov/publications/p587" },
  { label: "IRS Topic No. 511 — Business Travel Expenses", url: "https://www.irs.gov/taxtopics/tc511" },
  { label: "IRC § 179 — Election to Expense Certain Business Assets", url: "https://www.law.cornell.edu/uscode/text/26/179" },
  { label: "EU VAT Directive 2006/112/EC — EUR-Lex", url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=celex%3A32006L0112" },
  { label: "EU Cross-border VAT Refunds (Directive 2008/9/EC)", url: "https://europa.eu/youreurope/business/taxation/vat/cross-border-vat/index_en.htm" },
  { label: "EU VAT Rules by Topic — European Commission", url: "https://taxation-customs.ec.europa.eu/taxation/vat/eu-vat-rule-topic_en" },
];

const REGION_LABELS: Record<Region, string> = {
  all: "All Regions",
  us: "United States (IRS)",
  eu: "European Union (VAT)",
};

function DeductibilityBadge({ value, status }: { value: string; status: PolicyStatus }) {
  const base = "inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold border";
  if (status === "warning") return <div className={`${base} bg-amber-50 text-amber-700 border-amber-200`}>{value}</div>;
  if (status === "info") return <div className={`${base} bg-purple-50 text-purple-700 border-purple-200`}>{value}</div>;
  return <div className={`${base} bg-blue-50 text-blue-700 border-blue-100`}>{value}</div>;
}

export function TaxPolicies() {
  const { can } = useCapabilities();
  const [region, setRegion] = useState<Region>("all");
  const [search, setSearch] = useState("");
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const canRead = can("read:tax_policies");
  const canWrite = can("write:tax_policies");

  // Page-level gate. In practice only auditors hit this branch — every
  // other role's default set includes read:tax_policies.
  if (!canRead) {
    return (
      <div
        role="alert"
        data-testid="page-access-denied"
        className="max-w-md mx-auto mt-12 bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
      >
        <h1 className="text-lg font-semibold text-slate-900">
          You don't have access to Tax Policies
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Ask an admin to grant you the <code>read:tax_policies</code>{" "}
          capability.
        </p>
      </div>
    );
  }

  const filtered = ALL_POLICIES.filter((p) => {
    const regionMatch =
      region === "all" ||
      (region === "us" && (p.region === "US" || p.region === "US & EU")) ||
      (region === "eu" && (p.region === "EU" || p.region === "US & EU"));
    const searchMatch =
      search === "" ||
      p.category.toLowerCase().includes(search.toLowerCase()) ||
      p.deductibility.toLowerCase().includes(search.toLowerCase());
    return regionMatch && searchMatch;
  });

  return (
    <div className="h-full flex flex-col gap-6 max-w-6xl mx-auto overflow-y-auto pb-6">
      {!canWrite && (
        <div
          data-testid="taxpolicies-readonly-banner"
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 text-blue-900 text-sm rounded-md"
        >
          <Info className="w-4 h-4 text-blue-600" />
          <span>You have view-only access to tax policies.</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tax Policies Mapping</h1>
          <p className="text-slate-500 mt-1">Real deductibility rules and VAT treatments sourced from IRS publications and EU VAT Directives</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-slate-100 rounded-lg p-1 gap-1">
            {(["all", "us", "eu"] as Region[]).map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  region === r
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {r === "all" ? "All" : r === "us" ? "🇺🇸 US" : "🇪🇺 EU"}
              </button>
            ))}
          </div>
          {canWrite && (
            <button
              data-testid="taxpolicies-new-policy"
              className="flex items-center gap-2 px-4 py-2 bg-[#0070F2] hover:bg-[#005BCA] text-white font-medium rounded-md shadow-sm transition-colors text-sm"
            >
              <Plus className="w-4 h-4" /> New Policy
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Summary / Alerts */}
        <div className="lg:col-span-1 flex flex-col gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-purple-600" /> Policy Engine Status
            </h3>
            <div className="flex items-center justify-between p-3 bg-purple-50 text-purple-800 rounded-xl border border-purple-100 mb-3">
              <span className="text-sm font-medium">Active Rules Applied</span>
              <span className="font-bold">{ALL_POLICIES.length}</span>
            </div>
            <div className="flex gap-2 mb-4">
              <div className="flex-1 p-2 bg-green-50 rounded-lg border border-green-100 text-center">
                <p className="text-lg font-bold text-green-700">{ALL_POLICIES.filter(p => p.status === "active").length}</p>
                <p className="text-[11px] text-green-600 font-medium">Active</p>
              </div>
              <div className="flex-1 p-2 bg-amber-50 rounded-lg border border-amber-100 text-center">
                <p className="text-lg font-bold text-amber-700">{ALL_POLICIES.filter(p => p.status === "warning").length}</p>
                <p className="text-[11px] text-amber-600 font-medium">Caution</p>
              </div>
              <div className="flex-1 p-2 bg-purple-50 rounded-lg border border-purple-100 text-center">
                <p className="text-lg font-bold text-purple-700">{ALL_POLICIES.filter(p => p.status === "info").length}</p>
                <p className="text-[11px] text-purple-600 font-medium">Info</p>
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Proofly automatically matches incoming expenses to these rules to calculate estimated tax deductions and flag missing documentation.
            </p>
            <button className="w-full py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 text-sm font-medium border border-slate-200 rounded-lg transition-colors">
              Run compliance check
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Recent Regulatory Updates
            </h3>
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-sm font-medium text-amber-900">IRS: Meals temporarily 100% deductible</p>
                <p className="text-xs text-amber-700 mt-1">The One Big Beautiful Bill Act (2025) restores the 100% restaurant-meal deduction for expenses Jan 1, 2025 – Dec 31, 2026.</p>
                <a
                  href="https://www.schwartzschwartz.com/blog/2025/7/6/new-rules-for-business-meal-deductions-what-just-changed-and-why-it-matters"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 mt-2 hover:text-amber-800 underline underline-offset-2"
                >
                  Read IRS guidance <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <p className="text-sm font-medium text-slate-900">EU VAT OSS / Cross-border digital services</p>
                <p className="text-xs text-slate-500 mt-1">EU One-Stop-Shop (OSS) rules simplify VAT compliance for businesses supplying digital services across member states.</p>
                <a
                  href="https://taxation-customs.ec.europa.eu/taxation/vat/eu-vat-rule-topic_en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 mt-2 hover:text-blue-700 underline underline-offset-2"
                >
                  EU Commission guidance <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-sm font-medium text-blue-900">IRS Section 179 limit raised to $1,220,000 for 2024</p>
                <p className="text-xs text-blue-700 mt-1">Immediate expensing now covers software, equipment, and qualifying business property placed in service in 2024.</p>
                <a
                  href="https://www.irs.gov/publications/p946"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 mt-2 hover:text-blue-800 underline underline-offset-2"
                >
                  IRS Pub. 946 <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>

          {/* References panel */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <BookOpen className="w-5 h-5 text-slate-600" /> Source References
            </h3>
            <ul className="space-y-2">
              {REFERENCES.map((ref) => (
                <li key={ref.url}>
                  <a
                    href={ref.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1.5 text-xs text-blue-600 hover:text-blue-800 hover:underline leading-snug"
                  >
                    <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {ref.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Right column: Policy Table */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search categories..."
                className="w-full pl-9 pr-4 py-1.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-shadow"
              />
            </div>
            <span className="text-xs text-slate-400 font-medium">{filtered.length} policies · {REGION_LABELS[region]}</span>
          </div>

          <div className="flex-1 overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-medium">Expense Category</th>
                  <th className="px-5 py-3 font-medium">Region</th>
                  <th className="px-5 py-3 font-medium">Deductibility</th>
                  <th className="px-5 py-3 font-medium">VAT Treatment</th>
                  <th className="px-5 py-3 font-medium">Required Docs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((policy, idx) => (
                  <Fragment key={idx}>
                    <tr
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 text-sm">{policy.category}</span>
                          {policy.status === "warning" && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                          {policy.status === "info" && <Info className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${
                          policy.region === "US"
                            ? "bg-blue-50 text-blue-700 border border-blue-100"
                            : policy.region === "EU"
                            ? "bg-yellow-50 text-yellow-700 border border-yellow-100"
                            : "bg-slate-100 text-slate-600 border border-slate-200"
                        }`}>
                          {policy.region}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <DeductibilityBadge value={policy.deductibility} status={policy.status} />
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-xs text-slate-600">{policy.vatRule}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1">
                          {policy.requirements.map((req, rIdx) => (
                            <span key={rIdx} className="inline-block px-2 py-0.5 bg-slate-100 text-slate-600 text-[11px] font-medium rounded border border-slate-200">
                              {req}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                    {expandedIdx === idx && (
                      <tr className="bg-slate-50/80">
                        <td colSpan={5} className="px-5 py-4">
                          <div className="flex flex-col gap-2">
                            {policy.note && (
                              <p className="text-xs text-slate-600 leading-relaxed">{policy.note}</p>
                            )}
                            <a
                              href={policy.referenceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            >
                              <BookOpen className="w-3 h-3" />
                              {policy.reference} <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="py-12 text-center text-slate-400 text-sm">No policies match your search.</div>
            )}
          </div>

          <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between text-xs text-slate-400">
            <span>Showing {filtered.length} of {ALL_POLICIES.length} policies</span>
            <span>Sources: IRS.gov, EUR-Lex, European Commission · Updated 2024–2025</span>
          </div>
        </div>
      </div>
    </div>
  );
}
